import React, { useEffect, useState, useContext } from 'react';
import { Grid, FormLabel, FormHelperText, CircularProgress } from '@material-ui/core';
import { SecondaryButton, TextBox, SingelSelect, Multiselect } from '../../../../Core/FormInput';
import AppContext from '../../../../App/AppContext';
import { getBranches } from '../../../../Core/Service/branches-service';
import { postTCards_CashAccountAddUpdate } from '../../../../Core/Service/TCard-CashAccounts-service';

const CashAccountModal = (props) => {
  const { showToast } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  
  const [state, setState] = useState({
    cashAccountCode: props.cashAccountCode || '',
    cashAccountID: props.cashAccountID || null,
    cashAccountBranchID: props.cashAccountBranchID || null,
    errors: {}
  });

  useEffect(() => {
    const fetchBranches = async () => {
      setLoading(true);
      try {
        const res = await getBranches();
        if (res.success) {
          const list = res.data.map(w => ({ id: w.id, name: w.branchShortName }));
          setAvailableBranches(list);
          
          if (props.cashAccountID) {
            const sel = props.cashAccountBranchID
              ? list.find(b => b.id === Number(props.cashAccountBranchID))
              : null;
            setState(st => ({ ...st, cashAccountBranchID: sel }));
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, [props.cashAccountBranchID, props.cashAccountID]);

  const handleInput = e => {
    const { name, value } = e.target;
    setState(st => ({
      ...st,
      [name]: value,
      errors: { ...st.errors, [name]: '' }
    }));
  };

  const singleSelectChange = nm => (_, val) => {
    setState(st => ({
      ...st,
      [nm]: val,
      errors: { ...st.errors, [nm]: '' }
    }));
  };

  const multiSelectChange = (_, value) => {
    setSelectedBranches(value);
  };

  const handleValidation = () => {
    const errs = {};
    let valid = true;

    if (!state.cashAccountCode) {
      errs.cashAccountCode = 'Cash Account Code is required.';
      valid = false;
    }
    
    if (props.cashAccountID) {
      if (!state.cashAccountBranchID?.id) {
        errs.branches = 'Please select a Branch.';
        valid = false;
      }
    } else {
      if (selectedBranches.length === 0) {
        errs.branches = 'Please select at least one branch.';
        valid = false;
      }
    }

    setState(st => ({ ...st, errors: errs }));
    return valid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;
    setSubmitting(true);
    
    try {
      if (props.cashAccountID) {
        const data = {
          cashAccountID: state.cashAccountID,
          cashAccountCode: state.cashAccountCode,
          cashAccountBranchID: state.cashAccountBranchID.id
        };
        const res = await postTCards_CashAccountAddUpdate(data);
        if (res?.data?.success) {
          showToast(`Cash Account Updated successfully`);
          props.onClose(true);
        } else {
          showToast(res?.message || 'Operation failed', 'error');
        }
      } else {
        const requests = selectedBranches.map(branch => {
          const payload = {
            cashAccountID: null,
            cashAccountCode: state.cashAccountCode,
            cashAccountBranchID: branch.id
          };
          return postTCards_CashAccountAddUpdate(payload);
        });

        const results = await Promise.allSettled(requests);
        
        let successCount = 0;
        const errorMessages = [];
        
        results.forEach((result, index) => {
          const branchName = selectedBranches[index].name;
          if (result.status === 'fulfilled') {
            const res = result.value;
            if (res.data?.success) {
              successCount++;
            } else {
              const msg = res.data?.message || 'Operation failed';
              errorMessages.push(`${branchName}: ${msg}`);
            }
          } else {
            errorMessages.push(`${branchName}: ${result.reason.message || 'Network error'}`);
          }
        });

        if (successCount === selectedBranches.length) {
          showToast(`Successfully created ${successCount} cash account(s)`);
          props.onClose(true);
        } else if (successCount > 0) {
          showToast(
            `Created ${successCount} account(s), failed for ${errorMessages.length} branch(es)`,
            'warning'
          );
          console.error('Failed creations:', errorMessages);
        } else {
          showToast('Failed to create any accounts', 'error');
          console.error('All creations failed:', errorMessages);
        }
      }
    } catch (error) {
      console.error('Unexpected submission error:', error);
      showToast('An unexpected error occurred', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {loading ? (
        <div style={{ margin: '79px 270px' }}>
          <CircularProgress style={{ marginLeft: -20 }} />
        </div>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormLabel required className="mandatoryfields">
              Cash Account Code
            </FormLabel>
            <TextBox
              autoFocus
              name="cashAccountCode"
              value={state.cashAccountCode}
              onChange={handleInput}
              inputProps={{ maxLength: 10 }}
            />
            {state.errors.cashAccountCode && (
              <FormHelperText error>{state.errors.cashAccountCode}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={12} className="mandatory-fields">
            <FormLabel required className="mandatoryfields">
              Branches
            </FormLabel>
            
            {props.cashAccountID ? (
              <SingelSelect
                options={availableBranches}
                value={state.cashAccountBranchID}
                onChange={singleSelectChange('cashAccountBranchID')}
              />
            ) : (
              <Multiselect
                options={availableBranches}
                value={selectedBranches}
                onChange={multiSelectChange}
              />
            )}
            
            {state.errors.branches && (
              <FormHelperText error>{state.errors.branches}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={12}>
            <SecondaryButton
              fullWidth
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </SecondaryButton>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default CashAccountModal;